import { Chess } from "#vendor/chessjs/chess.js";
import type { Move } from "#vendor/chessjs/chess.js";
import { createStore } from "./createStore.ts";
import { userSlice, isAuthenticated } from "./slices/user.ts";
import type { UserState } from "./slices/user.ts";
import { bookSlice } from "./slices/book.ts";
import { myGamesSlice } from "./slices/myGames.ts";
import { repertoireSlice } from "./slices/repertoire.ts";
import { engineSlice } from "./slices/engine.ts";
import { boardSlice } from "./slices/board.ts";
import { configSlice } from "./slices/config.ts";
import { studyLineSlice } from "./slices/studyLine.ts";
import { practiceSlice } from "./slices/practice.ts";
import { progressSlice } from "./slices/progress.ts";
import { linesSlice } from "./slices/lines.ts";
import { studyListSlice } from "./slices/studyList.ts";
import { boardsSlice } from "./slices/boards.ts";
import { playSlice } from "./slices/play.ts";
import { memorySlice } from "./slices/memory.ts";
import { positionsSlice } from "./slices/positions.ts";
import { repertoiresSlice } from "./slices/repertoires.ts";
import { blindfoldSlice } from "./slices/blindfold.ts";

// Storage keys - static constants, not reactive
export const STORAGE_KEYS = {
    config: "sparring.chess.Config",
    collapse: "sparring.chess.ConfigCollapsed",
    repertoireCollapse: "sparring.chess.RepertoireCollapsed",
    playerCollapse: "sparring.chess.PlayerCollapsed",
    autoPlay: "sparring.chess.AutoPlay",
    onboardingCollapse: "sparring.chess.OnboardingCollapsed",
    practiceLog: "sparring.chess.PracticeLog",
    recentLines: "sparring.chess.RecentLines",
    resume: "sparring.chess.Resume",
    analysisOpen: "sparring.chess.AnalysisOpen",
    verdictDetailsOpen: "sparring.chess.VerdictDetailsOpen",
    // The study's engine and repertoire, as the reader left them (components/study/LivePanel.tsx)
    engineEvalHidden: "sparring.chess.HideEngineEval",
    engineLines: "sparring.chess.EngineLines",
    repertoireMovesOpen: "sparring.chess.RepertoireMovesOpen",
    lastGamesShown: "sparring.chess.LastGamesShown",
    linesWindow: "sparring.chess.LinesWindow",
    reportMoves: "sparring.chess.ReportMoves",
    exploreOrder: "sparring.chess.ExploreOrder",
    reportSeenJob: "sparring.chess.ReportSeenJob",
    analysisWindow: "sparring.chess.AnalysisWindow",
    analysisRecordMin: "sparring.chess.AnalysisRecordMin",
    anonymousId: "sparring.chess.AnonymousId",
    adminUserColumns: "sparring.chess.AdminUserColumns",
    // Per tab and per tool, not per device: the practice on the board of THIS tab, so walking over
    // to another page and back keeps it. The tool is in the name because the next ones will have
    // their own, and a memory game is not a practice of the study.
    practiceSnapshot: "sparring.chess.study.PracticeInProgress",
};

// Default values - static constants, not reactive
export const DEFAULTS = {
    speeds: ["blitz", "rapid", "classical"],
    ratings: ["1800", "2000", "2200"],
    engineEnabled: false,
    engineSource: "browser",
    engineDepth: "20",
    engineMultiPV: "3",
    boardSide: "white",
    boardAutoplay: true,
};

// Chess game instance - kept outside store (mutable, synchronous access needed)
export const game = new Chess();

// Combine all slices into initial state
const initialState = {
    user: { ...userSlice },
    book: { ...bookSlice },
    myGames: { ...myGamesSlice },
    repertoire: { ...repertoireSlice },
    engine: { ...engineSlice },
    board: {
        ...boardSlice,
        baseTurn: game.turn(), // Initialize with current game turn
    },
    config: { ...configSlice },
    studyLine: { ...studyLineSlice },
    practice: { ...practiceSlice },
    progress: { ...progressSlice },
    lines: { ...linesSlice },
    studyList: { ...studyListSlice },
    boards: { ...boardsSlice },
    play: { ...playSlice },
    memory: { ...memorySlice },
    positions: { ...positionsSlice },
    repertoires: { ...repertoiresSlice },
    blindfold: { ...blindfoldSlice },
};

// Create the main store instance
export const store = createStore(initialState);

// Debug: expose store on window for console access
window.__store = store;

// Helper functions for common operations

/**
 * Check if the current user is authenticated
 * @returns {boolean}
 */
export function isUserAuthenticated() {
    return isAuthenticated(store.get<UserState>('user'));
}

/**
 * Get UCI move history as space-separated string (e.g., "e2e4 e7e5 g1f3")
 */
export function getUciMoveHistory() {
    // The verbose form answers with move objects; the plain one with SAN strings.
    const history = game.history({ verbose: true }) as Move[];
    return history
        .map((move) => {
            let uci = move.from + move.to;
            if (move.promotion) {
                uci += move.promotion;
            }
            return uci;
        })
        .join(" ");
}

/**
 * Get SAN move history as space-separated string (e.g., "e4 d5 exd5")
 * This matches how games are stored in the database from Lichess
 */
export function getSanMoveHistory() {
    return game.history().join(" ");
}

// Re-export createStore for testing or custom stores
export { createStore };
